/**
 * StepProgress.tsx — the numbered-node progress bar (desktop) and dot track (mobile) from the
 * VER-128 canvas. `stepCount` excludes the trailing "ready" tick — the canvas's step counter
 * reads "Step N of 4" through the form steps, then the 5th node is the completion mark.
 */
import React from "react";
import { Box, Stack } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

type StepProgressProps = {
    /** 1-based current step, including the trailing "ready" step. */
    currentStep: number;
    /** Total steps including "ready" — 5 in this flow. */
    totalSteps: number;
    variant: "desktop" | "mobile";
};

export const StepProgress: React.FC<StepProgressProps> = ({ currentStep, totalSteps, variant }) => {
    const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);

    if (variant === "mobile") {
        return (
            <Stack direction="row" spacing={0.75} alignItems="center">
                {steps.map((n) => {
                    const done = n < currentStep;
                    const active = n === currentStep;
                    return (
                        <Box
                            key={n}
                            sx={{
                                height: 10,
                                width: active ? 22 : 10,
                                borderRadius: 999,
                                backgroundColor: done ? "primary.main" : active ? "secondary.main" : "background.paper",
                                border: "1px solid",
                                borderColor: done ? "primary.main" : active ? "secondary.main" : "borderShade.main",
                                transition: "width 160ms ease, background-color 160ms ease",
                            }}
                        />
                    );
                })}
            </Stack>
        );
    }

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {steps.map((n) => {
                const done = n < currentStep;
                const active = n === currentStep;
                const isLast = n === totalSteps;
                return (
                    <Stack
                        key={n}
                        direction="row"
                        alignItems="center"
                        flex={isLast ? "0 0 auto" : "1 1 auto"}
                        spacing={1}
                    >
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 13,
                                fontWeight: 700,
                                border: "1px solid",
                                borderColor: done ? "primary.main" : active ? "secondary.main" : "borderShade.main",
                                backgroundColor: done ? "primary.main" : active ? "secondary.main" : "background.paper",
                                color: done || active ? "common.white" : "text.secondary",
                            }}
                        >
                            {done || isLast ? <CheckIcon sx={{ fontSize: 16 }} /> : n}
                        </Box>
                        {!isLast && (
                            <Box
                                sx={{
                                    flex: "1 1 auto",
                                    height: 2,
                                    backgroundColor: n < currentStep ? "primary.main" : "borderShade.main",
                                }}
                            />
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
};
